import React from 'react';
import { TicketSLA, TicketStatus } from '../../../types/ticketInterfaces';

/* ════════════════════════════════════════════════════════════
   Утилиты расчёта дедлайнов
   ════════════════════════════════════════════════════════════ */

interface DeadlineState {
  /** Сколько секунд осталось до дедлайна. Отрицательное — просрочено. */
  remainingSec: number;
  /** Уже зафиксирован реальный момент закрытия этого дедлайна. */
  closed: boolean;
  /** Пометка, что дедлайн нарушен. */
  breached: boolean;
}

const calcState = (
  deadline: string | null,
  closedAt: string | null,
  breached: boolean,
): DeadlineState | null => {
  if (!deadline) return null;
  const dl = new Date(deadline).getTime();
  const now = Date.now();
  // Если дедлайн уже закрыт фактом — фиксируем «closed» и считаем
  // оставшееся как diff между фактом закрытия и дедлайном (это полезно
  // для индикации «успели на 5 мин» / «не успели»).
  if (closedAt) {
    const closed = new Date(closedAt).getTime();
    return {
      remainingSec: Math.round((dl - closed) / 1000),
      closed: true,
      breached,
    };
  }
  return {
    remainingSec: Math.round((dl - now) / 1000),
    closed: false,
    breached: breached || dl < now,
  };
};

/** Цвет/тон по состоянию дедлайна. Палитра под общую: зелёный, жёлтый, красный. */
const tone = (st: DeadlineState | null) => {
  if (!st) return { color: '#9CA3AF', bg: '#F3F4F6' };       // нет SLA — серый
  if (st.closed) {
    if (st.breached) return { color: '#B42318', bg: '#FDEAEA' };
    return { color: '#1F7A3A', bg: '#E8F6EE' };              // успели — зелёный
  }
  if (st.breached || st.remainingSec < 0) {
    return { color: '#B42318', bg: '#FDEAEA' };              // просрочено — красный
  }
  // Жёлтый, если осталось < 30 минут (универсальный порог для compact).
  if (st.remainingSec < 30 * 60) {
    return { color: '#B26B00', bg: '#FFF4E5' };
  }
  return { color: '#1F7A3A', bg: '#E8F6EE' };                // зелёный
};

/** Человекочитаемое «осталось 1ч 23м» / «просрочено 5м». */
const formatRemaining = (st: DeadlineState | null): string => {
  if (!st) return '—';
  const abs = Math.abs(st.remainingSec);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);

  let body: string;
  if (h >= 24) {
    const d = Math.floor(h / 24);
    body = `${d}д ${h % 24}ч`;
  } else if (h > 0) {
    body = `${h}ч ${m}м`;
  } else {
    body = `${m}м`;
  }

  if (st.closed) {
    return st.breached ? `просрочен на ${body}` : `закрыт за ${body} до дедлайна`;
  }
  if (st.remainingSec < 0) return `просрочено на ${body}`;
  return `осталось ${body}`;
};

/* ════════════════════════════════════════════════════════════
   Компактный режим — для колонки в списке тикетов
   ════════════════════════════════════════════════════════════ */

interface CompactProps {
  sla: TicketSLA | null;
  ticketStatus: TicketStatus;
}

/**
 * Маленький бейдж «SLA: осталось 2ч». Если статус тикета solved/closed
 * — показываем «✓ в SLA» / «✗ просрочен», иначе живой обратный отсчёт.
 *
 * Сначала смотрим на resolution (главный SLA), потом на first_response —
 * именно тот, что ещё не закрыт, должен подсвечиваться в первую очередь.
 */
function SlaIndicatorCompact({ sla, ticketStatus }: CompactProps) {
  if (!sla) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  // Решённые/закрытые тикеты: показываем итог по resolution, а не
  // живой таймер (он уже не идёт).
  if (ticketStatus === 'solved' || ticketStatus === 'closed') {
    const st = calcState(sla.resolution_deadline, sla.resolved_at, sla.resolution_breached);
    const t = tone(st);
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
        style={{ color: t.color, backgroundColor: t.bg }}
      >
        {st?.breached ? '✗' : '✓'} SLA
      </span>
    );
  }

  // Открытый тикет: первый ответ ещё не дан — подсвечиваем first_response;
  // иначе подсвечиваем resolution.
  const showFirst = !sla.first_response_at;
  const st = showFirst
    ? calcState(sla.first_response_deadline, null, sla.first_response_breached)
    : calcState(sla.resolution_deadline, null, sla.resolution_breached);
  const t = tone(st);
  const label = showFirst ? 'Ответ' : 'Решение';
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ color: t.color, backgroundColor: t.bg }}
      title={`${label}: ${formatRemaining(st)}`}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: t.color }} />
      {label}: {formatRemaining(st)}
    </span>
  );
}

/* ════════════════════════════════════════════════════════════
   Подробный режим — для сайдбара детальной страницы
   ════════════════════════════════════════════════════════════ */

interface DetailedProps {
  sla: TicketSLA | null;
  ticketStatus: TicketStatus;
}

const Row: React.FC<{
  label: string;
  st: DeadlineState | null;
  deadline: string | null;
}> = ({ label, st, deadline }) => {
  const t = tone(st);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <p className="text-[#bcbcbc] font-semibold text-sm">{label}</p>
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ color: t.color, backgroundColor: t.bg }}
        >
          {st?.closed ? (st.breached ? 'Просрочен' : 'В SLA') : formatRemaining(st)}
        </span>
      </div>
      <p className="text-[#242426] text-sm font-medium">
        {deadline
          ? new Date(deadline).toLocaleString('ru-RU', {
              dateStyle: 'short',
              timeStyle: 'short',
            })
          : '—'}
      </p>
    </div>
  );
};

/**
 * Карточка SLA — две строки. Полностью совместима по визуалу с
 * блоками HelpdeskTicket-сайдбара (label сверху серым, значение снизу).
 */
function SlaIndicatorDetailed({ sla, ticketStatus }: DetailedProps) {
  if (!sla) {
    return <p className="text-sm text-[#bcbcbc]">SLA для этой заявки не настроен.</p>;
  }

  const closed = ticketStatus === 'solved' || ticketStatus === 'closed';

  const firstSt = calcState(
    sla.first_response_deadline,
    sla.first_response_at,
    sla.first_response_breached,
  );
  const resolutionSt = calcState(
    sla.resolution_deadline,
    closed ? sla.resolved_at : null,
    sla.resolution_breached,
  );

  return (
    <div className="flex flex-col gap-4">
      <Row
        label="Первый ответ"
        st={firstSt}
        deadline={sla.first_response_deadline}
      />
      <Row
        label="Решение"
        st={resolutionSt}
        deadline={sla.resolution_deadline}
      />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   Публичный API: одна точка входа с режимом
   ════════════════════════════════════════════════════════════ */

interface SlaIndicatorProps {
  sla: TicketSLA | null;
  ticketStatus: TicketStatus;
  variant?: 'compact' | 'detailed';
}

const SlaIndicator: React.FC<SlaIndicatorProps> = ({
  sla,
  ticketStatus,
  variant = 'compact',
}) => {
  if (variant === 'detailed') {
    return <SlaIndicatorDetailed sla={sla} ticketStatus={ticketStatus} />;
  }
  return <SlaIndicatorCompact sla={sla} ticketStatus={ticketStatus} />;
};

export default SlaIndicator;
